import React, { useEffect, useState } from "react";
import AppShell from "../components/AppShell";
import PageHero from "../components/PageHero";
import { api, formatError } from "../lib/api";
import { toast } from "sonner";
import { Link } from "react-router-dom";
import {
  Users as UsersIcon, Eye, EyeOff, Globe, Instagram, Linkedin, MapPin,
} from "lucide-react";

// Moved out of Account page in iter 37 so it has its own permanent home in
// the sidebar (My Presence → Community profile).
const DIR_CATEGORIES = ["food", "beverage", "supplement", "wellness", "beauty", "pet", "other"];
const DIR_STAGES = [
  ["idea", "Idea"], ["pre-launch", "Pre-launch"], ["launched", "Launched"],
  ["in-retail", "In retail"], ["scaling", "Scaling"],
];

export default function CommunityProfile() {
  const [profile, setProfile] = useState(null);
  const [busy, setBusy] = useState(false);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    setBusy(true);
    api.get("/auth/me")
      .then((r) => {
        const dir = r.data?.directory || {};
        setProfile({
          brand: dir.brand || "", bio: dir.bio || "", city: dir.city || "",
          category: dir.category || "", stage: dir.stage || "",
          website: dir.website || "", instagram: dir.instagram || "",
          linkedin: dir.linkedin || "",
          published: !!r.data?.directory_published,
        });
      })
      .catch(() => setProfile({
        brand: "", bio: "", city: "", category: "", stage: "",
        website: "", instagram: "", linkedin: "", published: false,
      }))
      .finally(() => setBusy(false));
  }, []);

  const set = (k, v) => setProfile((p) => ({ ...p, [k]: v }));

  const save = async (publish) => {
    if (!profile) return;
    setSaving(true);
    try {
      const body = { ...profile, published: publish !== undefined ? publish : profile.published };
      const { data } = await api.put("/auth/profile/directory", body);
      setProfile((p) => ({ ...p, published: !!data.published }));
      toast.success(data.published ? "Live in the directory" : "Saved — not yet published");
    } catch (e) { toast.error(formatError(e)); }
    finally { setSaving(false); }
  };

  if (busy || !profile) {
    return <AppShell title="Community profile"><div className="w-full p-6">Loading…</div></AppShell>;
  }
  const canPublish = profile.brand.trim() && profile.category;

  return (
    <AppShell title="Community profile">
      <div className="w-full" id="directory" data-testid="community-profile-page">
        <PageHero
          eyebrow="Community Profile"
          icon={UsersIcon}
          title={<>Show up in the <span className="text-grad">Members Directory</span></>}
          subtitle={<>Fill this out and hit Publish to be discoverable by other founders inside the Institute. Your profile shows on <Link to="/members" className="text-primary underline underline-offset-2">/members</Link>.</>}
          right={
            <span data-testid="community-profile-status"
              className={["inline-flex items-center gap-1 text-[11px] font-bold uppercase tracking-widest rounded-full px-3 py-1",
                profile.published ? "bg-primary/10 text-primary" : "bg-muted text-muted-foreground"].join(" ")}>
              {profile.published ? (<><Eye className="w-3 h-3" /> Live</>) : (<><EyeOff className="w-3 h-3" /> Hidden</>)}
            </span>
          }
        />

        <div className="surface p-6">
          <div className="grid md:grid-cols-2 gap-4">
            <LabeledInput label="Brand" value={profile.brand} onChange={(v) => set("brand", v)}
              testid="community-brand" placeholder="Your brand name" required />
            <LabeledInput label="City" value={profile.city} onChange={(v) => set("city", v)}
              testid="community-city" placeholder="Los Angeles, CA" icon={MapPin} />
            <div>
              <p className="text-[10px] uppercase tracking-widest text-muted-foreground font-semibold mb-1.5">
                Category<span className="text-primary"> *</span>
              </p>
              <select value={profile.category} onChange={(e) => set("category", e.target.value)}
                data-testid="community-category"
                className="w-full rounded-lg border border-shell-border bg-white px-3 py-2 text-sm focus:outline-none focus:border-primary">
                <option value="">Pick a category</option>
                {DIR_CATEGORIES.map((c) => <option key={c} value={c}>{c.charAt(0).toUpperCase() + c.slice(1)}</option>)}
              </select>
            </div>
            <div>
              <p className="text-[10px] uppercase tracking-widest text-muted-foreground font-semibold mb-1.5">Stage</p>
              <select value={profile.stage} onChange={(e) => set("stage", e.target.value)}
                data-testid="community-stage"
                className="w-full rounded-lg border border-shell-border bg-white px-3 py-2 text-sm focus:outline-none focus:border-primary">
                <option value="">Pick a stage</option>
                {DIR_STAGES.map(([v, label]) => <option key={v} value={v}>{label}</option>)}
              </select>
            </div>
            <div className="md:col-span-2">
              <p className="text-[10px] uppercase tracking-widest text-muted-foreground font-semibold mb-1.5">Short bio</p>
              <textarea rows={3} value={profile.bio} onChange={(e) => set("bio", e.target.value.slice(0, 400))}
                data-testid="community-bio"
                placeholder="One paragraph about your brand and what you're building right now."
                className="w-full rounded-lg border border-shell-border bg-white px-3 py-2 text-sm focus:outline-none focus:border-primary" />
              <p className="text-[10px] text-muted-foreground text-right mt-1">{profile.bio.length}/400</p>
            </div>
            <LabeledInput label="Website" value={profile.website} onChange={(v) => set("website", v)}
              testid="community-website" placeholder="https://…" icon={Globe} />
            <LabeledInput label="Instagram" value={profile.instagram} onChange={(v) => set("instagram", v)}
              testid="community-instagram" placeholder="@yourbrand" icon={Instagram} />
            <LabeledInput label="LinkedIn" value={profile.linkedin} onChange={(v) => set("linkedin", v)}
              testid="community-linkedin" placeholder="in/warren-ezra" icon={Linkedin} />
          </div>

          <div className="mt-6 flex items-center justify-end gap-2 flex-wrap">
            <button onClick={() => save(false)} disabled={saving} data-testid="community-save"
              className="text-xs font-semibold text-muted-foreground hover:text-foreground border border-shell-border rounded-full px-4 py-2">
              Save draft
            </button>
            <button onClick={() => save(!profile.published)} disabled={saving || (!profile.published && !canPublish)}
              data-testid="community-publish"
              className="btn-primary text-sm disabled:opacity-60">
              {saving ? "Saving…" : profile.published ? "Unpublish" : "Publish to directory"}
            </button>
          </div>
          {!canPublish && !profile.published && (
            <p className="mt-2 text-[11px] text-muted-foreground text-right">
              Brand and category are required to publish.
            </p>
          )}
        </div>
      </div>
    </AppShell>
  );
}

function LabeledInput({ label, value, onChange, placeholder, icon: Icon, testid, required }) {
  return (
    <div>
      <p className="text-[10px] uppercase tracking-widest text-muted-foreground font-semibold mb-1.5">
        {label}{required && <span className="text-primary"> *</span>}
      </p>
      <div className="relative">
        {Icon && <Icon className="w-4 h-4 absolute left-3 top-2.5 text-muted-foreground" />}
        <input value={value} onChange={(e) => onChange(e.target.value)} placeholder={placeholder}
          data-testid={testid}
          className={["w-full rounded-lg border border-shell-border bg-white py-2 pr-3 text-sm focus:outline-none focus:border-primary",
            Icon ? "pl-9" : "pl-3"].join(" ")} />
      </div>
    </div>
  );
}
